import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  BarChart3, 
  LineChart, 
  PieChart, 
  TrendingUp, 
  Download, 
  Calendar,
  Filter,
  RefreshCw,
  Activity,
  Users,
  Database,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { apiClient } from '../../services/api-client';

interface UsageData {
  total_documents: number;
  total_searches: number;
  total_embeddings: number;
  active_users: number;
  daily_requests: number;
  weekly_requests: number;
  monthly_requests: number;
}

interface PerformanceData {
  average_response_time: number;
  search_success_rate: number;
  embedding_generation_time: number;
  vector_search_time: number;
  database_query_time: number;
}

interface TimeSeriesData {
  timestamp: string;
  value: number;
  metric_type: string;
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
    fill?: boolean;
  }[];
}

const Analytics: React.FC<{ className?: string }> = ({ className }) => {
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [performance, setPerformance] = useState<PerformanceData | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24h');
  const [selectedMetric, setSelectedMetric] = useState<string>('requests');
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'trends' | 'kpis'>('overview');

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageRes, perfRes, tsRes] = await Promise.all([
        apiClient.get<UsageData>('/api/v1/analytics/usage'),
        apiClient.get<PerformanceData>('/api/v1/analytics/performance'),
        apiClient.get<TimeSeriesData[]>(`/api/v1/monitoring/metrics?metric_type=${selectedMetric}`),
      ]);
      setUsage(usageRes);
      setPerformance(perfRes);
      setTimeSeries(tsRes);
    } catch (err) {
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, [selectedMetric]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(loadAnalytics, 300000);
    return () => clearInterval(interval);
  }, []);

  const getKpiStatus = (value: number, threshold: number) => {
    if (value >= threshold) return 'good';
    if (value >= threshold * 0.8) return 'warning';
    return 'critical';
  };

  const getKpiIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
      case 'critical':
        return <XCircle className="h-6 w-6 text-red-500" />;
      default:
        return <Activity className="h-6 w-6 text-gray-500" />;
    }
  };

  const getKpiColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const exportReport = () => {
    if (!usage || !performance) return;
    
    const report = {
      timestamp: new Date().toISOString(),
      timeRange: selectedTimeRange,
      usage: usage,
      performance: performance,
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className={`analytics-content ${className || ''}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`analytics-content ${className || ''}`}>
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold mb-2">Error Loading Analytics Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadAnalytics} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`analytics-content ${className || ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive data analysis and insights</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Button onClick={loadAnalytics} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={exportReport} variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
        <Button
          variant={activeTab === 'overview' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('overview')}
          className="flex items-center gap-2"
        >
          <BarChart3 className="h-4 w-4" />
          Overview
        </Button>
        <Button
          variant={activeTab === 'performance' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('performance')}
          className="flex items-center gap-2"
        >
          <Activity className="h-4 w-4" />
          Performance
        </Button>
        <Button
          variant={activeTab === 'trends' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('trends')}
          className="flex items-center gap-2"
        >
          <TrendingUp className="h-4 w-4" />
          Trends
        </Button>
        <Button
          variant={activeTab === 'kpis' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('kpis')}
          className="flex items-center gap-2"
        >
          <Target className="h-4 w-4" />
          KPIs
        </Button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'overview' && usage && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Documents</p>
                    <p className="text-2xl font-bold text-blue-600">{usage?.total_documents?.toLocaleString() || '0'}</p>
                  </div>
                  <Database className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Searches</p>
                    <p className="text-2xl font-bold text-green-600">{usage?.total_searches?.toLocaleString() || '0'}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Users</p>
                    <p className="text-2xl font-bold text-purple-600">{usage?.active_users || '0'}</p>
                  </div>
                  <Users className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Daily Requests</p>
                    <p className="text-2xl font-bold text-orange-600">{usage?.daily_requests?.toLocaleString() || '0'}</p>
                  </div>
                  <Activity className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      {activeTab === 'performance' && performance && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Average Response Time</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {performance?.average_response_time ? (performance.average_response_time * 1000).toFixed(0) : '0'}ms
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Search Success Rate</p>
                  <p className="text-2xl font-bold text-green-600">
                    {performance?.search_success_rate ? (performance.search_success_rate * 100).toFixed(1) : '0'}%
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-600">Embedding Generation</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {performance?.embedding_generation_time ? (performance.embedding_generation_time * 1000).toFixed(0) : '0'}ms
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      {activeTab === 'trends' && timeSeries.length > 0 && (
        <div className="space-y-6">
          <div className="flex gap-2 mb-4">
            {['requests', 'response_time', 'error_rate'].map(metric => (
              <Button
                key={metric}
                variant={selectedMetric === metric ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSelectedMetric(metric)}
              >
                {metric.replace('_', ' ')}
              </Button>
            ))}
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LineChart className="h-5 w-5" />
                Time Series Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                  <LineChart className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Time series chart would be rendered here</p>
                  <p className="text-sm text-gray-400">Showing {selectedMetric} over time</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Analytics; 